import { PDFDocument } from "pdf-lib";

type PdfData = string | Uint8Array | ArrayBuffer;

const toBlob = (data: PdfData): Blob => {
	if (typeof data === "string") {
		const base64 = data.replace(/^data:application\/pdf;base64,/, "");
		const binaryString = globalThis.atob(base64);
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- atob guarantees indices within range
		const bytes = Uint8Array.from(binaryString, (c) => c.codePointAt(0)!);
		return new Blob([bytes], { type: "application/pdf" });
	}
	return new Blob([data as BlobPart], { type: "application/pdf" });
};

/**
 * Opens the PDF in a new browser tab using the browser's native PDF viewer.
 * The user can print from there using Ctrl+P or the viewer's print button.
 * This avoids all iframe cross-origin issues and PDF.js worker complexity.
 */
export const printPdf = (data: PdfData): void => {
	const blobUrl = URL.createObjectURL(toBlob(data));
	const tab = globalThis.open(blobUrl, "_blank");
	if (!tab) {
		// Popup blocked — fall back to navigating the current tab
		globalThis.location.href = blobUrl;
	}
	// Revoke after a generous delay so the browser finishes loading it
	setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
};

export const mergePdfs = async (pdfs: PdfData[]): Promise<Uint8Array> => {
	const mergedPdf = await PDFDocument.create();

	for (const data of pdfs) {
		try {
			const pdf = await PDFDocument.load(data);
			const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
			for (const page of copiedPages) {
				mergedPdf.addPage(page);
			}
		} catch (error) {
			console.error("Error adding PDF to merge:", error);
		}
	}

	return mergedPdf.save();
};
